import { useEffect, useState } from "react";
import { Shield, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function TwoFactorSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [copied, setCopied] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const iniciarSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
      setConfirmCode("");
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmar = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/confirm", { code: confirmCode.trim() });
      toast.success("Verificación en dos pasos activada");
      setSetup(null);
      setConfirmCode("");
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const desactivar = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", {
        password: disablePassword,
        code: disableCode.trim() || undefined,
      });
      toast.success("2FA desactivado");
      setDisablePassword("");
      setDisableCode("");
      setSetup(null);
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copiarCodigos = async () => {
    if (!setup?.recovery_codes?.length) return;
    try {
      await navigator.clipboard.writeText(setup.recovery_codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  const enabled = !!status?.enabled;
  const pending = !!status?.pending || !!setup;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <Shield className="mt-0.5 h-5 w-5 shrink-0 text-zinc-700" strokeWidth={1.5} />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <p className="text-sm font-medium text-zinc-900">Verificación en dos pasos</p>
            <p className="mt-1 text-xs text-zinc-500">
              Opcional. Usa una app autenticadora (Google Authenticator, Microsoft Authenticator, Authy…)
              para pedir un código al iniciar sesión.
            </p>
            <p className="mt-2 text-xs font-medium text-zinc-700">
              Estado:{" "}
              {enabled ? (
                <span className="text-emerald-700">Activo</span>
              ) : pending ? (
                <span className="text-amber-700">Pendiente de confirmar</span>
              ) : (
                <span className="text-zinc-500">Desactivado</span>
              )}
              {enabled && status?.recovery_remaining != null && (
                <span className="ml-2 font-normal text-zinc-500">
                  · {status.recovery_remaining} códigos de recuperación
                </span>
              )}
            </p>
          </div>

          {!enabled && !setup && (
            <Button type="button" disabled={busy} onClick={iniciarSetup} className="bg-zinc-900 hover:bg-zinc-800">
              {busy ? "Preparando…" : "Activar 2FA"}
            </Button>
          )}

          {setup && !enabled && (
            <div className="space-y-4 rounded-xl border border-zinc-100 bg-zinc-50/80 p-3">
              <p className="text-xs text-zinc-600">
                Escanea el código QR con tu app autenticadora o introduce el secreto manualmente.
              </p>
              {setup.qr_png_base64 && (
                <img
                  src={`data:image/png;base64,${setup.qr_png_base64}`}
                  alt="Código QR 2FA"
                  className="mx-auto h-48 w-48 rounded-lg border border-zinc-200 bg-white p-2"
                />
              )}
              <div className="space-y-1">
                <Label className="text-xs">Secreto (manual)</Label>
                <code className="block break-all rounded-md bg-white px-2 py-1.5 text-xs text-zinc-800 border border-zinc-200">
                  {setup.secret}
                </code>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Códigos de recuperación</Label>
                  <button
                    type="button"
                    onClick={copiarCodigos}
                    className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <p className="text-[11px] text-amber-800">
                  Guárdalos en un lugar seguro. Cada uno solo se puede usar una vez si pierdes el móvil.
                </p>
                <ul className="grid grid-cols-2 gap-1 font-mono text-xs">
                  {(setup.recovery_codes || []).map((c) => (
                    <li key={c} className="rounded bg-white px-2 py-1 border border-zinc-200">
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="totp-confirm" className="text-xs">
                  Código de 6 dígitos
                </Label>
                <Input
                  id="totp-confirm"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                  maxLength={8}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={busy || confirmCode.trim().length < 6} onClick={confirmar}>
                  Confirmar y activar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    setSetup(null);
                    setConfirmCode("");
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {enabled && (
            <div className="space-y-3 rounded-xl border border-zinc-100 bg-zinc-50/80 p-3">
              <p className="text-xs text-zinc-600">
                Para desactivar, introduce tu contraseña y un código de la app (o uno de recuperación).
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs">Contraseña</Label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Código</Label>
                <Input
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  placeholder="6 dígitos o código de recuperación"
                  autoComplete="one-time-code"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={busy || !disablePassword || !disableCode.trim()}
                onClick={desactivar}
                className="border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                Desactivar 2FA
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
